import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import SectionHeading from '@/components/feature/SectionHeading';
import FilterChip from '@/components/base/FilterChip';
import { movies } from '@/mocks/movies';
import { useSeo } from '@/hooks/useSeo';

const POPULAR_FALLBACK = [...movies].sort((a, b) => b.views - a.views).slice(0, 4);

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get('q')?.trim() ?? '';
  const [keyword, setKeyword] = useState(q);
  const [activeGenres, setActiveGenres] = useState<string[]>([]);

  useSeo(
    q ? `${q} - 搜索结果 - 十八禁影视` : '搜索 - 十八禁影视',
    q
      ? `关于“${q}”的搜索结果，展示匹配片名与简介的十八禁影视作品，并支持进一步按类型筛选。`
      : '在十八禁影视大全中搜索影片、剧集及相关关键词。',
  );

  const matched = useMemo(() => {
    if (!q) return [];
    const lower = q.toLowerCase();
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(lower) ||
        m.synopsis.toLowerCase().includes(lower) ||
        m.director.toLowerCase().includes(lower) ||
        m.genres.some((g) => g.toLowerCase().includes(lower)),
    );
  }, [q]);

  const availableGenres = useMemo(
    () => Array.from(new Set(matched.flatMap((m) => m.genres))),
    [matched],
  );

  const results = useMemo(
    () => (activeGenres.length > 0 ? matched.filter((m) => m.genres.some((g) => activeGenres.includes(g))) : matched),
    [matched, activeGenres],
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const next = keyword.trim();
    setActiveGenres([]);
    navigate(next ? `/search?q=${encodeURIComponent(next)}` : '/search');
  };

  const toggleGenre = (genre: string) => {
    setActiveGenres((prev) => (prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]));
  };

  return (
    <div className="w-full pt-16 md:pt-20">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 md:pb-10 bg-background-100/50 border-b border-background-200/60">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>
        <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">
          {q ? `“${q}” 的搜索结果` : '搜索影视作品'}
        </h1>

        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入片名、类型、导演或关键词"
              className="w-full bg-background-100 border border-background-200 rounded-md pl-11 pr-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm rounded-md px-7 py-3 whitespace-nowrap cursor-pointer transition-colors"
          >
            搜索
          </button>
        </form>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        {q ? (
          results.length > 0 ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <p className="text-sm text-foreground-600">
                  共 <span className="font-label text-accent-400 font-bold">{results.length}</span> 条结果
                </p>
                {availableGenres.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-foreground-500">按类型进一步筛选：</span>
                    {availableGenres.map((g) => (
                      <FilterChip key={g} label={g} active={activeGenres.includes(g)} onClick={() => toggleGenre(g)} />
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {results.map((movie, i) => (
                  <MovieCard key={movie.id} movie={movie} index={i} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <span className="mx-auto w-14 h-14 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                <i className="ri-search-eye-line text-2xl text-foreground-500" />
              </span>
              <p className="text-foreground-800 text-base">没有找到与“{q}”相关的作品</p>
              <p className="mt-2 text-sm text-foreground-500">试试更短的关键词，或从下方热门作品中挑选。</p>
              <div className="mt-10">
                <SectionHeading eyebrow="POPULAR" title="热门影视推荐" moreTo="/category" />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5 text-left">
                  {POPULAR_FALLBACK.map((movie, i) => (
                    <MovieCard key={movie.id} movie={movie} index={i} />
                  ))}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="text-center py-16">
            <span className="mx-auto w-14 h-14 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
              <i className="ri-search-line text-2xl text-foreground-500" />
            </span>
            <p className="text-foreground-800 text-base">输入关键词，开始探索影视作品</p>
            <p className="mt-2 text-sm text-foreground-500">支持按片名、类型、导演与简介进行模糊匹配。</p>
            <div className="mt-10">
              <SectionHeading eyebrow="TRENDING" title="不妨先看看热门" moreTo="/category" />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5 text-left">
                {POPULAR_FALLBACK.map((movie, i) => (
                  <MovieCard key={movie.id} movie={movie} index={i} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}